import {CartItemInterface, Customer} from '@/__generated__/__types__';
import {ConversionCart, MetaEventList} from '@/_application/modules/tracking/meta/model/meta-conversion';
import {
    AddToCartCommandInput,
    ConversionApiCommandInput,
    ConversionApiEventType,
    ConversionCustomerType,
} from '@kidslabs/mario-client-event-tracking';
import crypto from 'crypto';

const hashingRequiredProperties = ['ph', 'em', 'fn', 'ln', 'ge', 'db', 'ct', 'st', 'zp', 'country'];

export const getHashedUserData = (user: ConversionCustomerType) => {
    const hashedProperties = hashingRequiredProperties.reduce((user_data, current) => {
        const value = user[current as keyof ConversionCustomerType];
        if (value) {
            return {...user_data, [current]: hashString(value)};
        }
        return user_data;
    }, {});
    return {
        ...user,
        ...hashedProperties,
    };
};

export const getConversionUserData = (customerData: Customer) => {
    const customer = {
        ph: customerData?.phone_number ?? '',
        em: customerData?.email?.toLowerCase() ?? '',
        fn: customerData?.firstname?.toLowerCase() ?? '',
        ln: customerData?.lastname?.toLowerCase() ?? '',
        ge: customerData?.gender ?? '',
        //db: customerData.date_of_birth ?? '',
        //ct: customerData.addresses ?? '',
        // st: customerData.rank ?? ''
        country: 'VN',
    } as ConversionCustomerType;

    return getHashedUserData(customer);
};

export function hashString(input: string, code: string = 'sha256') {
    return crypto.createHash(code).update(input).digest('hex');
}

export const getCustomContentByEventName = (eventName: MetaEventList, input: any) => {
    switch (eventName) {
        case MetaEventList.VIEW_CONTENT:
            return formatDataForViewContentEvent(input);
        case MetaEventList.SEARCH:
            return formatDataForSearchEvent(input);
        case MetaEventList.INITIATE_CHECKOUT:
            return formatDataForInitialCheckoutEvent(input);
        case MetaEventList.PURCHASE:
            return formatDataForPurchaseEvent(input);
        case MetaEventList.PAGE_VIEW:
            return formatDataForPageViewEvent(input);
        case MetaEventList.VIEW_CATEGORY:
            return formatDataForViewCategoryEvent(input);
        case MetaEventList.REGISTRATION:
            return formatDataForRegistrationEvent();
        case MetaEventList.ADD_TO_CART:
            return formatDataForAddToCartEvent(input);
        default:
            return {};
    }
};

export const getContentIdFromCartItems = (items: CartItemInterface[]) => {
    return items.filter(item => item).map(item => `${item.product?.id ?? 'no-cart-item-product-id'}`) ?? [];
};

export const generateConversionContentsFromCartItems = (items: CartItemInterface[]) => {
    const contents: ConversionCart[] = items
        .filter(item => item)
        .map(item => {
            return {
                id: `${item.product?.id ?? 'no-cart-item-product-id'}`,
                quantity: item.quantity ?? 0,
                item_price: item.prices?.price.value ?? 0,
            };
        });

    return contents ?? [];
};

export const getConversionApiInput = (eventData: ConversionApiEventType) => {
    let conversionApiInput: ConversionApiCommandInput;

    // if (!process.env.NEXT_PUBLIC_CONVERSION_API_TEST_CODE) {
    //   conversionApiInput = {
    //     data: [eventData],
    //   };
    // } else {
    //   conversionApiInput = {
    //     data: [eventData],
    //     test_event_code: process.env.NEXT_PUBLIC_CONVERSION_API_TEST_CODE,
    //   };
    // }
    conversionApiInput = {
        data: [eventData],
    };

    return conversionApiInput;
};

export const formatDataForViewContentEvent = (input: {productName: string; productId: string}) => {
    return {
        content_name: input.productName,
        content_ids: [input.productId],
        content_type: 'product',
    };
};

export const formatDataForSearchEvent = (input: string) => {
    return {
        search_string: input,
    };
};

export const formatDataForInitialCheckoutEvent = (input: {
    cartItems: CartItemInterface[];
    cartTotalPrice: number;
    cartTotalAmount: number;
}) => {
    return {
        content_ids: getContentIdFromCartItems(input.cartItems),
        currency: 'VND',
        content_type: 'product',
        value: input.cartTotalPrice,
        num_items: JSON.stringify(input.cartTotalAmount),
        contents: generateConversionContentsFromCartItems(input.cartItems),
    };
};

export const formatDataForPurchaseEvent = (input: {
    cartItems: CartItemInterface[];
    cartTotalPrice: number;
    cartTotalAmount: number;
    orderId: string;
}) => {
    return {
        content_ids: getContentIdFromCartItems(input.cartItems),
        currency: 'VND',
        content_type: 'product',
        value: input.cartTotalPrice,
        order_id: input.orderId,
        contents: generateConversionContentsFromCartItems(input.cartItems),
    };
};

export const formatDataForPageViewEvent = (input: string) => {
    return {
        custom_properties: {
            page_title: input,
        },
    };
};

export const formatDataForViewCategoryEvent = (input: {categoryName: string}) => {
    return {
        content_category: input.categoryName,
    };
};

export const formatDataForRegistrationEvent = () => {
    return {
        status: 'completed',
        registration_method: 'otp',
    };
};

export const formatDataForAddToCartEvent = (input: AddToCartCommandInput) => {
    return input;
};

export const getMetaConversionUrl = () => {
    //@: v21.0
    const API_VERSION = process.env.NEXT_PUBLIC_CONVERSION_API_APP_VERSION;
    //@: Pixel's id. Recommend use same id with Facebook Pixel
    const PIXEL_ID = process.env.NEXT_PUBLIC_CONVERSION_API_PIXEL_ID;
    //@: Token to authenticate valid request to meta
    const TOKEN = process.env.NEXT_PUBLIC_CONVERSION_API_ACCESS_TOKEN;

    if (API_VERSION && PIXEL_ID && TOKEN) {
        return `https://graph.facebook.com/${API_VERSION}/${PIXEL_ID}/events?access_token=${TOKEN}`;
    }
};
